import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  ClipboardList,
  Droplets,
  Repeat,
  Drill,
  FlaskConical,
  ShieldCheck,
  ShieldAlert,
  MessagesSquare,
  ClipboardCheck,
  HardHat,
  ListChecks,
  Clock3,
  Wrench,
  ArrowRightLeft,
  CalendarDays,
  Gauge,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import { trackFeatureOpened } from "@/lib/telemetry";

// Reports & Field Documentation — hub page.
// Organised into three visual groups (Group 01 contains an internal subgroup for
// BOP & Well Control Test Records):
//   1. Formal Drilling & Operations Reports  (includes BOP & Well Control sub-group)
//   2. Safety, Briefings & Job Planning
//   3. Downtime, Equipment & Handover

const FORMAL_REPORTS = [
  { kind: "card", id: "daily-drilling-report", name: "Daily Drilling Report", short: "DDR",
    description: "Capture rough rig-floor notes and generate a polished, industry-standard-style daily drilling report.",
    icon: ClipboardList, route: "/reports/daily-drilling-report" },
  { kind: "card", id: "daily-mud-report", name: "Daily Mud Report", short: "DMR",
    description: "Mud properties, treatments, pit volumes, gains/losses, and next-tour recommendations.",
    icon: Droplets, route: "/reports/daily-mud-report" },
  { kind: "card", id: "trip-sheet", name: "Trip Sheet", short: "TRIP",
    description: "Trip in/out, trip-tank volumes, fill-up vs displacement, flow checks — factual record only.",
    icon: Repeat, route: "/reports/trip-sheet" },
  { kind: "card", id: "bit-run-record", name: "Bit Run Record", short: "BIT",
    description: "Bit details, BHA, run summary, drilling parameters, performance, dull grade, and recommendations.",
    icon: Drill, route: "/reports/bit-run-record" },
  { kind: "card", id: "cementing-report", name: "Cementing Report", short: "CMT",
    description: "Slurry design, displacement plan, pressures, returns, and post-job recommendations.",
    icon: FlaskConical, route: "/reports/cementing-report" },
  { kind: "divider", id: "subgroup-bop-wcr", label: "BOP & Well Control Test Records" },
  { kind: "card", id: "bop-test-sheet", name: "BOP Test Sheet", short: "BOP",
    description: "Records BOP test data as user-entered values only. This tool does not certify pass / fail.",
    icon: ShieldCheck, route: "/reports/bop-test-sheet" },
  { kind: "card", id: "surface-bop-test-report", name: "Surface BOP Test Report", short: "SFBOP",
    description: "Surface stack, choke/kill, manifold, accumulator/closing unit, test plug/cup tester, flow checks. Record-support only — does not certify BOP condition.",
    icon: ShieldCheck, route: "/reports/surface-bop-test-report" },
  { kind: "card", id: "subsea-bop-test-report", name: "Subsea BOP Test Report", short: "SBOP",
    description: "Subsea stack, control pods, choke/kill, accumulator, ROV intervention. Record-support only — does not certify BOP condition.",
    icon: ShieldCheck, route: "/reports/subsea-bop-test-report" },
  { kind: "card", id: "hse-incident-form", name: "HSE Incident / Near Miss Form", short: "HSE",
    description: "Facts-only draft form. No blame, no severity classification — follow company and regulatory reporting procedures.",
    icon: ShieldAlert, route: "/reports/hse-incident-form" },
];

const SAFETY_REPORTS = [
  { id: "toolbox-talk", name: "Toolbox Talk Generator", short: "TBT",
    description: "Pre-work safety talk. Topic, hazards, controls, PPE, stop-work triggers, and discussion points.",
    icon: MessagesSquare, route: "/reports/toolbox-talk" },
  { id: "pre-tour-briefing", name: "Pre-Tour Briefing", short: "PTB",
    description: "Incoming-tour brief: current operation, planned activities, risks, permits, weather, and stop-work triggers.",
    icon: ClipboardCheck, route: "/reports/pre-tour-briefing" },
  { id: "pre-job-briefing", name: "Pre-Job Briefing", short: "PJB",
    description: "Task-specific brief: step-by-step plan, hazards, controls, PPE, permits, energy isolation, and emergency response.",
    icon: HardHat, route: "/reports/pre-job-briefing" },
  { id: "jsa-generator", name: "JSA / Job Safety Analysis Draft", short: "JSA",
    description: "Steps · hazards · controls · residual risk (user-entered) · PPE · permits. Draft support only — does not approve work.",
    icon: ListChecks, route: "/reports/jsa-generator" },
];

const COMING_SOON = [];

const KICK_SHEETS = [
  { id: "surface-kick-sheet-vertical", name: "Surface Kick Sheet — Vertical Well", short: "KS-SV",
    description: "Surface kick-sheet documentation for vertical wells. No lateral fields. Calculation-support only — does not certify kill MW, MAASP, shoe integrity, or well-control decisions.",
    icon: ShieldAlert, route: "/reports/surface-kick-sheet-vertical" },
  { id: "surface-kick-sheet-horizontal", name: "Surface Kick Sheet — Horizontal Well", short: "KS-SH",
    description: "Surface kick-sheet documentation for horizontal wells. Kick location is user-entered only; no operational instructions.",
    icon: ShieldAlert, route: "/reports/surface-kick-sheet-horizontal" },
  { id: "subsea-kick-sheet-vertical", name: "Subsea Kick Sheet — Vertical Well", short: "KS-XV",
    description: "Subsea kick-sheet documentation for vertical wells. No lateral fields. Riser / choke / kill line + line friction recorded as user-entered; tool does not auto-correct subsea pressures.",
    icon: ShieldAlert, route: "/reports/subsea-kick-sheet-vertical" },
  { id: "subsea-kick-sheet-horizontal", name: "Subsea Kick Sheet — Horizontal Well", short: "KS-XH",
    description: "Subsea kick-sheet documentation for horizontal wells. Kick location is user-entered only; no kill sequence or choke schedule generated.",
    icon: ShieldAlert, route: "/reports/subsea-kick-sheet-horizontal" },
];

const OPS_REPORTS = [
  { id: "downtime-report", name: "Downtime Report", short: "NPT",
    description: "Per-event downtime / NPT log: category, duration, suspected cause (user-entered, under review), corrective actions, recommendations.",
    icon: Clock3, route: "/reports/downtime-report" },
  { id: "equipment-failure-report", name: "Equipment Failure Report", short: "EQF",
    description: "Equipment tag, failure description, troubleshooting, parts replaced, post-repair status. Does not certify fitness-for-service.",
    icon: Wrench, route: "/reports/equipment-failure-report" },
  { id: "handover-notes", name: "Handover Notes", short: "HND",
    description: "Outgoing → incoming personnel summary: current operation, planned activities, open concerns, watch items.",
    icon: ArrowRightLeft, route: "/reports/handover-notes" },
  { id: "daily-operations-summary", name: "Daily Operations Summary", short: "DOS",
    description: "Lightweight one-page daily ops summary for company-man / client distribution. Complements (not replaces) the DDR.",
    icon: CalendarDays, route: "/reports/daily-operations-summary" },
  { id: "lot-fit-report", name: "LOT / FIT Report", short: "LOT",
    description: "Leak-Off Test / Formation Integrity Test record. Record-support only — does not certify formation integrity or pass/fail.",
    icon: Gauge, route: "/reports/lot-fit-report" },
];

function ReportCard({ r, onClick }) {
  const Icon = r.icon || FileText;
  return (
    <button
      key={r.id}
      type="button"
      data-testid={`report-card-${r.id}`}
      onClick={() => { trackFeatureOpened(r.id, "report"); onClick(); }}
      className="card-rs p-4 flex items-start gap-3 text-left transition-colors hover:border-[var(--rs-amber)]"
    >
      <div
        className="w-10 h-10 rounded-md inline-flex items-center justify-center shrink-0"
        style={{ background: "rgba(255,179,0,0.10)", border: "1px solid rgba(255,179,0,0.35)" }}
      >
        <Icon className="w-5 h-5" style={{ color: "var(--rs-amber)" }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-heading font-black text-base tracking-tight">{r.name}</div>
          <span
            className="font-mono-rs text-[9px] tracking-[0.18em] uppercase px-1.5 py-[1px] rounded-[3px] font-bold"
            style={{
              background: "rgba(255,179,0,0.14)",
              color: "var(--rs-amber)",
              border: "1px solid rgba(255,179,0,0.4)",
            }}
          >
            {r.short}
          </span>
        </div>
        <p className="text-[12px] text-[var(--rs-text-3)] mt-1 leading-snug">{r.description}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] shrink-0 mt-1" />
    </button>
  );
}

function DisabledCard({ r }) {
  const Icon = r.icon || FileText;
  return (
    <div
      key={r.id}
      data-testid={`report-card-soon-${r.id}`}
      aria-disabled="true"
      className="card-rs p-4 flex items-start gap-3 text-left opacity-60 cursor-not-allowed select-none"
      style={{ background: "rgba(22,24,29,0.45)" }}
    >
      <div
        className="w-10 h-10 rounded-md inline-flex items-center justify-center shrink-0"
        style={{ background: "rgba(160,160,160,0.08)", border: "1px dashed var(--rs-border)" }}
      >
        <Icon className="w-5 h-5 text-[var(--rs-text-3)]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-heading font-black text-base tracking-tight text-[var(--rs-text-2)]">{r.name}</div>
          <span
            className="font-mono-rs text-[9px] tracking-[0.18em] uppercase px-1.5 py-[1px] rounded-[3px] font-bold"
            style={{
              background: "rgba(160,160,160,0.10)",
              color: "var(--rs-text-3)",
              border: "1px dashed var(--rs-text-3)",
            }}
          >
            {r.short} · Coming Soon
          </span>
        </div>
        <p className="text-[12px] text-[var(--rs-text-3)] mt-1 leading-snug">{r.description}</p>
      </div>
    </div>
  );
}

function GroupHeading({ eyebrow, title, blurb, testId }) {
  return (
    <div data-testid={testId} className="pt-1">
      <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">{eyebrow}</div>
      <h2 className="font-heading font-black text-xl tracking-tight mt-1">{title}</h2>
      {blurb && <p className="text-[12px] text-[var(--rs-text-3)] mt-1 leading-snug">{blurb}</p>}
    </div>
  );
}

export default function Reports() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-5 bg-grain pb-2" data-testid="reports-hub-page">
      <section className="pt-1">
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">
          Documentation
        </div>
        <h1 className="font-heading font-black text-3xl tracking-tight mt-1" data-testid="reports-hub-title">
          Reports &amp; Field Documentation
        </h1>
        <p className="text-sm text-[var(--rs-text-2)] mt-1.5">
          Field-floor documentation tools. Rough notes in — clean, professional report out. Review under company procedures before submission.
        </p>
      </section>

      {/* Global disclaimer card */}
      <div
        data-testid="reports-disclaimer"
        className="card-rs p-3 flex items-start gap-2.5"
        style={{
          background: "rgba(22,24,29,0.65)",
          borderColor: "var(--rs-border)",
          borderLeft: "2px solid var(--rs-amber)",
        }}
      >
        <AlertTriangle className="w-3.5 h-3.5 mt-[3px] shrink-0" style={{ color: "var(--rs-amber)" }} />
        <p className="text-[11px] leading-snug text-[var(--rs-text-3)]">
          <span className="font-semibold" style={{ color: "var(--rs-text-2)" }}>
            Draft documentation support only.
          </span>{" "}
          RigSentinel field-documentation tools provide draft support only. All toolbox talks, briefings, JSAs, risk assessments, and operational documents must be reviewed, corrected, and approved by competent site personnel under company procedures, permit-to-work requirements, client requirements, and applicable regulations before use.
        </p>
      </div>

      {/* Group 1 — Formal Drilling & Operations Reports */}
      <GroupHeading
        testId="reports-group-formal"
        eyebrow="Group 01"
        title="Formal Drilling & Operations Reports"
        blurb="Structured operational reports for drilling, mud, trips, bits, cementing, BOP test and HSE."
      />
      <div className="flex flex-col gap-3" data-testid="reports-list-formal">
        {FORMAL_REPORTS.map((r) =>
          r.kind === "divider" ? (
            <div
              key={r.id}
              data-testid={`reports-subgroup-${r.id}`}
              className="pt-2 pb-0.5 flex items-center gap-2"
            >
              <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-text-3)]">
                {r.label}
              </div>
              <div className="flex-1 h-px" style={{ background: "var(--rs-border)" }} />
            </div>
          ) : (
            <ReportCard key={r.id} r={r} onClick={() => navigate(r.route)} />
          )
        )}
      </div>

      {/* Group 2 — Safety, Briefings & Job Planning */}
      <GroupHeading
        testId="reports-group-safety"
        eyebrow="Group 02"
        title="Safety, Briefings & Job Planning"
        blurb="Pre-work safety talks, tour/job briefings, and JSA drafting. Drafts only — site approval required."
      />
      <div className="flex flex-col gap-3" data-testid="reports-list-safety">
        {SAFETY_REPORTS.map((r) => (
          <ReportCard key={r.id} r={r} onClick={() => navigate(r.route)} />
        ))}
      </div>

      {/* Group 3 — Downtime, Equipment & Handover */}
      <GroupHeading
        testId="reports-group-ops"
        eyebrow="Group 03"
        title="Downtime, Equipment & Handover"
        blurb="Downtime, equipment failure, handover, daily ops summary, and LOT / FIT record-support."
      />
      <div className="flex flex-col gap-3" data-testid="reports-list-ops">
        {OPS_REPORTS.map((r) => (
          <ReportCard key={r.id} r={r} onClick={() => navigate(r.route)} />
        ))}
      </div>

      {/* Group 4 — Well Control Support Documents */}
      <GroupHeading
        testId="reports-group-wellctl"
        eyebrow="Group 04"
        title="Well Control Support Documents"
        blurb="Kick Sheets — well-control documentation & calculation-support only. Never live operational instructions."
      />
      <div className="flex flex-col gap-3" data-testid="reports-list-wellctl">
        {KICK_SHEETS.map((r) => (
          <ReportCard key={r.id} r={r} onClick={() => navigate(r.route)} />
        ))}
      </div>

      {COMING_SOON.length > 0 && (
        <>
          <GroupHeading
            testId="reports-group-soon"
            eyebrow="Group 04 · Roadmap"
            title="Coming Soon"
            blurb="Planned for an upcoming release."
          />
          <div className="flex flex-col gap-3" data-testid="reports-list-soon">
            {COMING_SOON.map((r) => (
              <DisabledCard key={r.id} r={r} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
